import { create } from 'zustand';
import { getGameMetadata, validateMetadata } from '@api/metadataApi';
import feedback from '@components/ui/general/feedback';
import { Storage } from '@storage';

/**
 * useMetadataStore - Manages game metadata and rules
 *
 * This store is responsible for:
 * - Fetching game metadata from backend once
 * - Providing stat constraints and game rules
 * - Helper methods for applying constraints
 * - Persisting to StorageManager (IndexedDB)
 *
 * No subscriptions needed - other stores call getState() to access metadata
 */
const useMetadataStore = create((set, get) => ({
  // --- STATE ---
  metadata: null,
  loading: false,
  loaded: false,
  error: null,
  version: 1.1, // Increment this to force cache refresh
  _isRefreshing: false, // Flag to prevent multiple simultaneous refresh attempts
  _refreshPromise: null, // Shared promise for concurrent refresh requests
  _initialized: false, // Flag to track if we've tried to load from cache

  // --- ACTIONS ---

  /**
   * Initialize store by loading from Storage if available
   * Called once when app starts (in Main.jsx)
   *
   * @param {string|null} remoteVersion - Version from /api/versions endpoint
   *
   * Flujo:
   * 1. Si remoteVersion coincide con cache → usar cache (sin fetch)
   * 2. Si remoteVersion es null o diferente → marcar para fetch on login
   */
  init: async (remoteVersion = null) => {
    const state = get();
    if (state._initialized) return;

    console.log('[📦 metadata] Initializing...');

    try {
      const cached = await Storage.get('metadata', 'current');
      const cachedVersion = cached?.version || '0.0.0';

      // Si tenemos versión remota y coincide con cache, usar cache sin fetch
      if (remoteVersion && cached?.data && cachedVersion === remoteVersion) {
        if (validateMetadata(cached.data)) {
          set({
            metadata: cached.data,
            loaded: true,
            _initialized: true
          });
          console.log(`[📦 metadata] Cache hit: v${cachedVersion}`);
          return;
        }
      }

      // Cache existe pero versión diferente, log it
      if (cached?.data && remoteVersion && cachedVersion !== remoteVersion) {
        console.log(`[📦 metadata] Version mismatch: cache v${cachedVersion}, remote v${remoteVersion}`);
      }
    } catch (error) {
      console.warn('[Metadata] Failed to check cache:', error);
    }

    set({ _initialized: true });
    console.log('[📦 metadata] No valid cache, will fetch on login');
  },

  /**
   * Fetch metadata from backend
   * Called on login/register (force=true) or when metadata is missing
   * @param {boolean} force - Force fetch even if already loaded
   */
  fetchMetadata: async (force = false) => {
    const state = get();

    // Don't fetch if already loaded or loading (unless forced)
    if (!force && (state.loaded || state.loading)) {
      return state.metadata;
    }

    console.log('[📦 metadata] Fetching from server...');
    set({ loading: true, error: null });

    try {
      const metadata = await getGameMetadata();

      // Validate the metadata structure
      if (!validateMetadata(metadata)) {
        throw new Error('Invalid metadata structure received');
      }

      set({
        metadata,
        loaded: true,
        loading: false,
        error: null
      });

      // Persist to Storage (async, don't block)
      const version = metadata.version || '1.0.0';
      Storage.set('metadata', 'current', { data: metadata, version }).catch(err => {
        console.warn('[Metadata] Failed to save to cache:', err);
      });

      console.log(`[📦 metadata] Loaded from server (v${version})`);
      return metadata;

    } catch (error) {
      console.error('[Metadata] Failed to load:', error);

      set({
        loading: false,
        error: error.message || 'Failed to load game metadata',
        loaded: false
      });

      // This is critical - game cannot function without metadata
      feedback.error('Failed to load game rules. Please refresh the page.');
      throw error;
    }
  },

  /**
   * Reset the store (useful for testing/logout)
   * Clears both memory and IndexedDB cache
   */
  reset: async () => {
    set({
      metadata: null,
      loading: false,
      loaded: false,
      error: null,
      _initialized: false
    });
    await Storage.clearCategory('metadata');
  },

  /**
   * Handle corrupted metadata by trying to refresh first, then redirecting to error page if refresh fails
   * @param {string} errorMessage - Description of what's wrong
   * @param {object} debugData - Additional data for debugging
   */
  _handleCorruptedMetadata: async (errorMessage, debugData = {}) => {
    const state = get();

    // If metadata was never loaded, trigger a load (not corrupted, just not ready)
    if (!state.loaded && !state.metadata) {
      console.warn('[MetadataStore] Metadata not loaded yet, triggering fetch:', errorMessage);
      // Trigger fetch but don't block - component will re-render when ready
      get().fetchMetadata(true).catch(err => {
        console.error('[MetadataStore] Failed to fetch metadata:', err);
      });
      return;
    }

    // If already loading, just wait
    if (state.loading) {
      console.debug('[MetadataStore] Metadata is loading, please wait...');
      return;
    }

    console.warn('[MetadataStore] Detected stale/corrupted metadata:', errorMessage);
    console.log('Debug data:', debugData);

    // Try to refresh metadata from server before giving up
    const refreshed = await get()._tryRefreshMetadata();

    if (refreshed) {
      console.info('[MetadataStore] Metadata refreshed successfully');
      // Don't reload - the refresh already updated the store
      // Components will re-render with new data
      return;
    }

    // Refresh failed - show detailed error and redirect
    console.error('❌ ============================================ ❌');
    console.error('❌ CORRUPTED METADATA - REFRESH FAILED ❌');
    console.error('❌ ============================================ ❌');
    console.error('Error:', errorMessage);
    console.error('📊 Current metadata store state:');
    console.log(get().metadata);
    console.error('🔍 Debug data:');
    console.log(debugData);
    console.error('❌ ============================================ ❌');

    // Redirect to error page
    window.location.href = '/metadata-error';
  },

  /**
   * Try to refresh metadata from server
   * Used when we detect stale/corrupted metadata
   * Returns a shared promise so concurrent callers wait for the same refresh
   * @returns {Promise<boolean>} True if refresh succeeded, false otherwise
   */
  _tryRefreshMetadata: () => {
    const state = get();

    // If refresh already in progress, return the existing promise
    // This ensures all concurrent callers wait for the same result
    if (state._isRefreshing && state._refreshPromise) {
      console.info('[MetadataStore] Refresh already in progress, waiting for existing refresh...');
      return state._refreshPromise;
    }

    console.info('[MetadataStore] Attempting to refresh stale metadata from server...');

    // Create the refresh promise
    const refreshPromise = (async () => {
      try {
        const metadata = await getGameMetadata();

        if (!validateMetadata(metadata)) {
          console.error('[MetadataStore] Refreshed metadata is still invalid');
          return false;
        }

        set({
          metadata,
          loaded: true,
          loading: false,
          error: null
        });

        // Persist to Storage
        try {
          const version = metadata.version || '1.0.0';
          await Storage.set('metadata', 'current', { data: metadata, version });
        } catch (err) {
          console.warn('[Metadata] Failed to save refreshed metadata:', err);
        }

        console.info('[Metadata] ✅ Metadata refreshed successfully');
        return true;

      } catch (error) {
        console.error('[MetadataStore] Failed to refresh metadata:', error);
        return false;
      } finally {
        // Clean up refresh state
        set({ _isRefreshing: false, _refreshPromise: null });
      }
    })();

    // Store the promise so concurrent callers can await it
    set({ _isRefreshing: true, _refreshPromise: refreshPromise });

    return refreshPromise;
  },

  // --- HELPER METHODS ---

  /**
   * Get constraint for a specific stat
   * @param {string} stat - The stat name
   * @returns {object} Constraint object with min/max values
   * @throws {Error} If metadata not loaded or stat constraint not found
   */
  getStatConstraint: (stat) => {
    const { metadata } = get();

    // FAIL LOUDLY: metadata must be loaded
    if (!metadata?.stat_constraints) {
      get()._handleCorruptedMetadata('metadata.stat_constraints is missing or undefined');
      throw new Error('[Metadata] stat_constraints not loaded. Call fetchMetadata() first.');
    }

    // FAIL LOUDLY: stat must exist in constraints
    if (!metadata.stat_constraints[stat]) {
      throw new Error(`[Metadata] No constraint found for stat: ${stat}`);
    }

    return metadata.stat_constraints[stat];
  },

  /**
   * Apply constraint to a stat value
   * @param {string} stat - The stat name
   * @param {number} value - The value to constrain
   * @returns {number} The constrained value
   */
  applyStatConstraint: (stat, value) => {
    const constraint = get().getStatConstraint(stat);

    if (!constraint) return value;

    let result = value;

    // Apply minimum constraint
    if (constraint.min !== null && constraint.min !== undefined) {
      result = Math.max(constraint.min, result);
    }

    // Apply maximum constraint
    if (constraint.max !== null && constraint.max !== undefined) {
      result = Math.min(constraint.max, result);
    }

    return result;
  },

  /**
   * Check if metadata is ready
   * @returns {boolean} True if metadata is loaded
   */
  isReady: () => {
    return get().loaded && get().metadata !== null;
  },

  /**
   * Get all tracked stats
   * @returns {array} List of stat names that should be tracked
   * @throws {Error} If metadata not loaded or tracked_stats invalid
   */
  getTrackedStats: () => {
    const { metadata } = get();

    // FAIL LOUDLY: metadata must be loaded
    if (!metadata?.tracked_stats) {
      get()._handleCorruptedMetadata('metadata.tracked_stats is missing or undefined');
      throw new Error('[Metadata] tracked_stats not loaded. Call fetchMetadata() first.');
    }

    // FAIL LOUDLY: must be valid array
    if (!Array.isArray(metadata.tracked_stats) || metadata.tracked_stats.length === 0) {
      get()._handleCorruptedMetadata(
        'metadata.tracked_stats is not a valid array or is empty',
        { tracked_stats: metadata.tracked_stats }
      );
      throw new Error('[Metadata] tracked_stats is invalid');
    }

    return metadata.tracked_stats;
  },

  // NOTE: getGameRules, getCombatRules, getEffectRules were removed (dead code)
  // Backend does not send these fields in metadata

  /**
   * Get effect by ID
   * @param {number} effectId - The effect ID
   * @returns {object} Effect object
   * @throws {Error} If metadata not loaded or effect not found
   */
  getEffectById: (effectId) => {
    const { metadata } = get();

    // FAIL LOUDLY: metadata must be loaded
    if (!metadata?.effects) {
      get()._handleCorruptedMetadata('metadata.effects array is missing or undefined');
      throw new Error('[Metadata] effects not loaded. Call fetchMetadata() first.');
    }

    const effect = metadata.effects.find(e => e.id === effectId);

    // FAIL LOUDLY: effect must exist
    if (!effect) {
      get()._handleCorruptedMetadata(
        `Effect ID ${effectId} not found in metadata (stale cache?)`,
        { effectId, availableEffectIds: metadata.effects.map(e => e.id) }
      );
      throw new Error(`[Metadata] Effect ID ${effectId} not found`);
    }

    // FAIL LOUDLY: effect must have critical fields
    const missingFields = [];
    if (!effect.id) missingFields.push('id');
    if (!effect.slug) missingFields.push('slug');
    if (!effect.name) missingFields.push('name');
    if (!effect.category) missingFields.push('category');
    if (!effect.category_color) missingFields.push('category_color');
    if (effect.properties === undefined) missingFields.push('properties');

    if (missingFields.length > 0) {
      get()._handleCorruptedMetadata(
        `Effect ${effectId} (${effect.slug || 'unknown'}) is missing critical fields: ${missingFields.join(', ')}`,
        { effect, missingFields }
      );
      throw new Error(`[Metadata] Effect ${effectId} missing fields: ${missingFields.join(', ')}`);
    }

    return effect;
  },

  /**
   * Get effect by slug
   * @param {string} effectSlug - The effect slug (e.g., 'sword_counter_attack')
   * @returns {object} Effect object
   * @throws {Error} If metadata not loaded or effect not found
   */
  getEffectBySlug: (effectSlug) => {
    const { metadata } = get();

    // FAIL LOUDLY: metadata must be loaded
    if (!metadata?.effects) {
      get()._handleCorruptedMetadata('metadata.effects array is missing or undefined');
      throw new Error('[Metadata] effects not loaded. Call fetchMetadata() first.');
    }

    const effect = metadata.effects.find(e => e.slug === effectSlug);

    // FAIL LOUDLY: effect must exist
    if (!effect) {
      get()._handleCorruptedMetadata(
        `Effect slug '${effectSlug}' not found in metadata (stale cache?)`,
        { effectSlug, availableEffectSlugs: metadata.effects.map(e => e.slug) }
      );
      throw new Error(`[Metadata] Effect slug '${effectSlug}' not found`);
    }

    // FAIL LOUDLY: effect must have critical fields
    const missingFields = [];
    if (!effect.id) missingFields.push('id');
    if (!effect.slug) missingFields.push('slug');
    if (!effect.name) missingFields.push('name');
    if (!effect.category) missingFields.push('category');
    if (!effect.category_color) missingFields.push('category_color');
    if (effect.properties === undefined) missingFields.push('properties');

    if (missingFields.length > 0) {
      get()._handleCorruptedMetadata(
        `Effect '${effectSlug}' (id: ${effect.id || 'unknown'}) is missing critical fields: ${missingFields.join(', ')}`,
        { effect, missingFields }
      );
      throw new Error(`[Metadata] Effect '${effectSlug}' missing fields: ${missingFields.join(', ')}`);
    }

    return effect;
  },

  /**
   * Get all effects
   * @returns {array} List of all effects
   * @throws {Error} If metadata not loaded
   */
  getAllEffects: () => {
    const { metadata } = get();

    // FAIL LOUDLY: metadata must be loaded
    if (!metadata?.effects) {
      throw new Error('[Metadata] effects not loaded. Call fetchMetadata() first.');
    }

    return metadata.effects;
  },

  /**
   * Get game configuration
   * @returns {object} Game configuration object
   * @throws {Error} If metadata not loaded
   */
  getGameConfig: () => {
    const { metadata } = get();

    // FAIL LOUDLY: metadata must be loaded
    if (!metadata?.game_config) {
      get()._handleCorruptedMetadata('metadata.game_config is missing or undefined');
      throw new Error('[Metadata] game_config not loaded. Call fetchMetadata() first.');
    }

    return metadata.game_config;
  },

  /**
   * Hydrate a unit effect with metadata template data
   * Adds missing category and properties from the effect template
   * @param {object} unitEffect - The unit effect to hydrate
   * @returns {object} The hydrated effect (mutates original)
   * @throws {Error} If unitEffect has no effect_id or template not found
   */
  hydrateUnitEffect: (unitEffect) => {
    // FAIL LOUDLY: must have effect_id
    if (!unitEffect?.effect_id) {
      throw new Error('[Metadata] Cannot hydrate effect without effect_id');
    }

    // getEffectById will throw if template not found (FAIL LOUDLY)
    const effectTemplate = get().getEffectById(unitEffect.effect_id);

    // Hydrate missing fields from template
    if (!unitEffect.effect_category && effectTemplate.category) {
      unitEffect.effect_category = effectTemplate.category;
    }

    // Only hydrate properties if completely missing
    // Unit-specific properties should override template defaults
    if (!unitEffect.properties && effectTemplate.properties) {
      unitEffect.properties = effectTemplate.properties;
    }

    // Hydrate nested effect object if missing (new structure)
    if (!unitEffect.effect && effectTemplate) {
      unitEffect.effect = effectTemplate;
    }

    // Also hydrate flat fields for backwards compatibility if needed
    if (!unitEffect.effect_name && effectTemplate.name) {
      unitEffect.effect_name = effectTemplate.name;
    }

    if (!unitEffect.effect_slug && effectTemplate.slug) {
      unitEffect.effect_slug = effectTemplate.slug;
    }

    // Hydrate stat_modifiers from template if not set on instance
    // UnitEffect.stat_modifiers can override template, but defaults to template values
    if (!unitEffect.stat_modifiers && effectTemplate.stat_modifiers) {
      unitEffect.stat_modifiers = effectTemplate.stat_modifiers;
    }

    // Hydrate interaction_tags from template for effect overlay detection
    if (!unitEffect.effect?.interaction_tags && effectTemplate.interaction_tags) {
      if (unitEffect.effect) {
        unitEffect.effect.interaction_tags = effectTemplate.interaction_tags;
      }
    }

    return unitEffect;
  },

  /**
   * Hydrate all effects for a unit
   * @param {object} unit - The unit with active_effects array
   * @returns {object} The unit with hydrated effects (mutates original)
   */
  hydrateUnitEffects: (unit) => {
    if (!unit?.active_effects || !Array.isArray(unit.active_effects)) {
      return unit;
    }

    unit.active_effects.forEach(unitEffect => {
      get().hydrateUnitEffect(unitEffect);
    });

    return unit;
  },

  // --- UNIT TEMPLATE HELPERS ---

  /**
   * Get all unit templates
   * @returns {array} List of all unit templates
   * @throws {Error} If metadata not loaded
   */
  getAllUnitTemplates: () => {
    const { metadata } = get();

    // FAIL LOUDLY: metadata must be loaded
    if (!metadata?.unit_templates) {
      throw new Error('[Metadata] unit_templates not loaded. Call fetchMetadata() first.');
    }

    return metadata.unit_templates;
  },

  /**
   * Get unit template by slug
   * @param {string} slug - The unit template slug (e.g., 'guardian', 'archer')
   * @returns {object} Unit template object
   * @throws {Error} If metadata not loaded or template not found
   */
  getUnitTemplateBySlug: (slug) => {
    const { metadata } = get();

    // FAIL LOUDLY: metadata must be loaded
    if (!metadata?.unit_templates) {
      throw new Error('[Metadata] unit_templates not loaded. Call fetchMetadata() first.');
    }

    const template = metadata.unit_templates.find(t => t.slug === slug);

    // FAIL LOUDLY: template must exist
    if (!template) {
      throw new Error(`[Metadata] Unit template not found for slug: ${slug}`);
    }

    return template;
  },

  /**
   * Get visual_data for a unit template by slug
   * @param {string} slug - The unit template slug
   * @returns {object} visual_data object
   * @throws {Error} If template not found or has no visual_data
   */
  getUnitVisualData: (slug) => {
    const template = get().getUnitTemplateBySlug(slug);

    // FAIL LOUDLY: visual_data must exist
    if (!template.visual_data) {
      throw new Error(`[Metadata] Unit template '${slug}' has no visual_data`);
    }

    return template.visual_data;
  },

  /**
   * Build a map of slug -> visual_data for all unit templates
   * Useful for batch lookups in components
   * @returns {object} Map of slug to visual_data
   * @throws {Error} If metadata not loaded
   */
  getVisualDataBySlug: () => {
    const templates = get().getAllUnitTemplates();
    const map = {};
    templates.forEach(t => {
      if (t.slug) {
        map[t.slug] = t.visual_data;
      }
    });
    return map;
  },

  // --- ENUM HELPERS ---
  // Enums are synced from backend PHP enums via metadata API
  // See: EnumCollectorService.php for list of exposed enums

  /**
   * Get an entire enum object by name
   * @param {string} enumName - The enum name (e.g., 'GameStatus', 'TeamClassification')
   * @returns {object} Enum object with KEY: value pairs
   * @throws {Error} If metadata not loaded or enum not found (FAIL LOUDLY)
   */
  getEnum: (enumName) => {
    const { metadata } = get();

    // FAIL LOUDLY: metadata must be loaded
    if (!metadata?.enums) {
      get()._handleCorruptedMetadata('metadata.enums is missing or undefined');
      throw new Error('[Metadata] Enums not loaded. Call fetchMetadata() first.');
    }

    // FAIL LOUDLY: enum must exist
    if (!metadata.enums[enumName]) {
      throw new Error(`[Metadata] Enum '${enumName}' not found. Available: ${Object.keys(metadata.enums).join(', ')}`);
    }

    return metadata.enums[enumName];
  },

  /**
   * Get a specific enum value by enum name and key
   * @param {string} enumName - The enum name (e.g., 'GameStatus')
   * @param {string} key - The enum key (e.g., 'LOBBY')
   * @returns {string} The enum value (e.g., 'game_lobby')
   * @throws {Error} If enum or key not found (FAIL LOUDLY)
   */
  getEnumValue: (enumName, key) => {
    const enumObj = get().getEnum(enumName);

    // FAIL LOUDLY: key must exist in enum
    if (!(key in enumObj)) {
      throw new Error(`[Metadata] Key '${key}' not found in enum '${enumName}'. Available: ${Object.keys(enumObj).join(', ')}`);
    }

    return enumObj[key];
  },

  /**
   * Get all available enum names
   * @returns {string[]} Array of enum names
   * @throws {Error} If metadata not loaded
   */
  getEnumNames: () => {
    const { metadata } = get();

    if (!metadata?.enums) {
      throw new Error('[Metadata] Enums not loaded. Call fetchMetadata() first.');
    }

    return Object.keys(metadata.enums);
  },

  // --- THEME HELPERS ---

  /**
   * Get available themes from metadata
   * @returns {array|null} List of themes or null if not available
   */
  getThemes: () => {
    const { metadata } = get();
    return metadata?.themes || null;
  }
}));

export default useMetadataStore;
